import { useCallback, useMemo, useState } from "react";
import type { Company, CompanyStatus } from "@kkp/api-client";

import {
  statusWeight,
  type Density,
  type SortDir,
  type SortKey,
} from "./shared";

export type StatusFilter = "All" | CompanyStatus;

export type CompanyStats = {
  total: number;
  active: number;
  inactive: number;
};

/** Pure company count summary powering the Clients stat cards. */
export function computeCompanyStats(companies: Company[]): CompanyStats {
  return {
    total: companies.length,
    active: companies.filter((c) => c.status === "Active").length,
    inactive: companies.filter((c) => c.status === "Inactive").length,
  };
}

export type CompaniesQuery = {
  query: string;
  statusFilter: StatusFilter;
  sortKey: SortKey;
  sortDir: SortDir;
};

/**
 * Pure search + status-filter + sort over the company list. Search matches name
 * or id (case-insensitive); `updatedAt` is a YYYY-MM-DD string so a lexicographic
 * compare sorts chronologically. Never mutates the input (sorts a copy).
 */
export function filterSortCompanies(
  companies: Company[],
  { query, statusFilter, sortKey, sortDir }: CompaniesQuery,
): Company[] {
  const q = query.trim().toLowerCase();
  let list = companies;

  if (q) {
    list = list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q),
    );
  }

  if (statusFilter !== "All") {
    list = list.filter((c) => c.status === statusFilter);
  }

  return [...list].sort((a, b) => {
    let cmp: number;
    if (sortKey === "name") {
      cmp = a.name.localeCompare(b.name);
    } else if (sortKey === "status") {
      cmp = statusWeight(a.status) - statusWeight(b.status);
    } else {
      // updatedAt as YYYY-MM-DD: lexicographic sort works.
      cmp = a.updatedAt.localeCompare(b.updatedAt);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });
}

/**
 * Clients-table view model shared by the admin and client Clients pages. Owns the
 * search / status-filter / sort / density / row-selection state and derives the
 * stat counts, the filtered+sorted rows, and the selection helpers from the
 * caller-supplied `companies`.
 *
 * Data loading and mutations intentionally stay in each app: their
 * `../../api/companies` layers + wiring diverge (admin surfaces the create
 * invite warning and invalidates the company-detail query; client does neither),
 * so the page keeps `companies` state + its own react-query/effect wiring and
 * the view stays dumb.
 */
export function useCompaniesTable(
  companies: Company[],
  initialStatus: StatusFilter = "All",
) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [density, setDensity] = useState<Density>("comfortable");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const stats = useMemo(() => computeCompanyStats(companies), [companies]);

  const filtered = useMemo(
    () =>
      filterSortCompanies(companies, { query, statusFilter, sortKey, sortDir }),
    [companies, query, statusFilter, sortKey, sortDir],
  );

  const selectedCount = useMemo(
    () => filtered.reduce((acc, r) => acc + (selected[r.id] ? 1 : 0), 0),
    [filtered, selected],
  );

  const isAllSelected = useMemo(
    () => filtered.length > 0 && filtered.every((r) => selected[r.id]),
    [filtered, selected],
  );

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = { ...prev };
      const willSelectAll = !isAllSelected;
      for (const r of filtered) next[r.id] = willSelectAll;
      return next;
    });
  }, [filtered, isAllSelected]);

  // Select a sort column, resetting to ascending; re-selecting the active column
  // flips the direction. Reads current state from the render closure.
  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  };

  const resetFilters = useCallback(() => {
    setQuery("");
    setStatusFilter("All");
    setSortKey("updatedAt");
    setSortDir("desc");
  }, []);

  const clearFilters = useCallback(() => {
    setQuery("");
    setStatusFilter("All");
  }, []);

  const rowPadY = density === "compact" ? "py-2" : "py-3";

  return {
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    sortKey,
    sortDir,
    density,
    setDensity,
    selected,
    setSelected,
    stats,
    filtered,
    selectedCount,
    isAllSelected,
    toggleAll,
    toggleSort,
    resetFilters,
    clearFilters,
    rowPadY,
  };
}
